// TryItForm — the real Try-it form. Renders path/query/header inputs,
// a JSON body editor with "Format" support, and Send / Copy as cURL / Share buttons.
//
// Validation: required path params must be non-empty. Send is disabled otherwise
// (or when there is no API key).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Loader2, Copy, Share2, KeyRound, ExternalLink, Wand2, MoreHorizontal } from 'lucide-react';
import { buildFormSpec, substitutePath, buildQueryString, type FormField } from '@/lib/openapi/form-builder';
import { useApiKey } from '@/stores/apiKey.store';
import { useTryRequest } from '@/hooks/useTryRequest';
import { useSharedRequest } from '@/hooks/useSharedRequest';
import { toCurl, toShareableUrl } from '@/lib/curl';
import { showToast } from '@/lib/toast';
import ResponseViewer from './ResponseViewer';
import RequestHistory from './RequestHistory';
import SharedRequestBanner from './SharedRequestBanner';
import { Select } from '@/components/ui/Select';
import { ActionMenu } from '@/components/ui/ActionMenu';
import type { OpenApiSpec, Operation } from '@/lib/openapi/types';
import { buildApiUrl } from '@/lib/api-url';

interface Props {
    operationId: string;
    method: string;
    path: string;
    op: Operation;
    spec: OpenApiSpec;
}

export default function TryItForm({ operationId, method, path, op, spec }: Props) {
    const { key } = useApiKey();
    const tryReq = useTryRequest();
    const form = useMemo(() => buildFormSpec(op, spec), [op, spec]);

    // Shared-link snapshot (captured once at mount; null when no/foreign hash).
    const { sharedState, consume } = useSharedRequest(operationId);
    const [bannerVisible, setBannerVisible] = useState<boolean>(() => sharedState !== null);
    const formRef = useRef<HTMLDivElement | null>(null);
    // Track whether we've already applied the shared snapshot — the body-sample
    // effect below runs on mount AND must not clobber the shared body. Using a
    // ref keeps the guard idempotent under StrictMode double-mount in dev.
    const sharedAppliedRef = useRef<boolean>(false);

    const [pathValues, setPathValues] = useState<Record<string, string>>(() => sharedState?.pathParams ?? {});
    const [queryValues, setQueryValues] = useState<Record<string, string>>(() => sharedState?.queryParams ?? {});
    const [queryIncluded, setQueryIncluded] = useState<Record<string, boolean>>({});
    const [headerValues, setHeaderValues] = useState<Record<string, string>>({});
    const [bodyText, setBodyText] = useState<string>(() => {
        if (sharedState && typeof sharedState.body === 'string') return sharedState.body;
        if (!form.hasBody) return '';
        try { return JSON.stringify(form.bodySample ?? {}, null, 2); }
        catch { return '{}'; }
    });
    const [bodyError, setBodyError] = useState<string | null>(null);

    // Re-derive body sample when op changes (e.g., navigating between endpoints).
    // On the very first mount with a shared snapshot we skip this so the shared
    // body wins; subsequent op changes proceed normally.
    useEffect(() => {
        if (sharedState && !sharedAppliedRef.current) {
            sharedAppliedRef.current = true;
            return;
        }
        if (!form.hasBody) { setBodyText(''); return; }
        try { setBodyText(JSON.stringify(form.bodySample ?? {}, null, 2)); }
        catch { setBodyText('{}'); }
        // sharedState intentionally omitted — captured once via ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.hasBody, form.bodySample]);

    // Initialise queryIncluded — include required by default, optional off.
    // When a shared snapshot supplied query params, auto-include those keys too
    // (otherwise the optional ones would appear disabled despite having values).
    useEffect(() => {
        const init: Record<string, boolean> = {};
        for (const p of form.queryParams) init[p.name] = p.required;
        if (sharedState?.queryParams) {
            for (const k of Object.keys(sharedState.queryParams)) init[k] = true;
        }
        setQueryIncluded(init);
        // sharedState is mount-captured; we want this to run only when fields change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.queryParams]);

    // Auto-scroll the form into view when arriving via a shared link.
    useEffect(() => {
        if (!sharedState) return;
        const el = formRef.current;
        if (!el) return;
        try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        catch { el.scrollIntoView(); }
        // Run once per mount; sharedState is stable for the lifetime of the form.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDismissBanner = () => {
        setBannerVisible(false);
        consume();
    };

    const missingRequired = form.pathParams.some((p) => p.required && !pathValues[p.name])
        || form.headerParams.some((p) => p.required && !headerValues[p.name]);
    const noKey = !key;
    const sendDisabled = tryReq.busy || missingRequired || noKey;

    const effectiveQueryValues = useMemo(() => {
        const out: Record<string, string> = {};
        for (const p of form.queryParams) {
            if (queryIncluded[p.name] && queryValues[p.name] !== undefined && queryValues[p.name] !== '') {
                out[p.name] = queryValues[p.name];
            }
        }
        return out;
    }, [form.queryParams, queryValues, queryIncluded]);

    const handleFormatJson = () => {
        if (!bodyText.trim()) { setBodyError(null); return; }
        try {
            const parsed = JSON.parse(bodyText);
            setBodyText(JSON.stringify(parsed, null, 2));
            setBodyError(null);
            showToast('JSON dirapikan');
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'JSON tidak valid';
            setBodyError(msg);
            showToast('JSON tidak valid');
        }
    };

    const handleSend = async () => {
        if (sendDisabled) return;
        // Validate body JSON if present.
        let bodyForSend = bodyText;
        if (form.hasBody && bodyText.trim().length > 0) {
            try { JSON.parse(bodyText); setBodyError(null); }
            catch (e: unknown) {
                const msg = e instanceof Error ? e.message : 'JSON tidak valid';
                setBodyError(msg);
                showToast('Body JSON tidak valid');
                return;
            }
        } else {
            bodyForSend = '';
        }

        await tryReq.submit({
            operationId,
            method,
            path,
            pathParams: pathValues,
            queryParams: effectiveQueryValues,
            headers: headerValues,
            body: bodyForSend,
        });
    };

    const buildPreviewUrl = (): string => {
        const finalPath = substitutePath(path, pathValues);
        return `${buildApiUrl(finalPath)}${buildQueryString(effectiveQueryValues)}`;
    };

    const handleCopyCurl = async () => {
        const headers: Record<string, string> = {};
        for (const [k, v] of Object.entries(headerValues)) {
            if (v) headers[k] = v;
        }
        if (key) headers['Authorization'] = `Bearer ${key}`;
        const hasBody = form.hasBody && bodyText.trim().length > 0 && method !== 'GET' && method !== 'DELETE';
        let bodyObj: unknown = undefined;
        if (hasBody) {
            try { bodyObj = JSON.parse(bodyText); }
            catch { bodyObj = bodyText; }
        }
        const curlStr = toCurl({ method, url: buildPreviewUrl(), headers, body: bodyObj });
        try {
            await navigator.clipboard.writeText(curlStr);
            showToast('cURL tersalin');
        } catch {
            showToast('Gagal menyalin');
        }
    };

    const handleShare = async () => {
        const url = toShareableUrl({
            operationId,
            formState: {
                pathParams: pathValues,
                queryParams: effectiveQueryValues,
                body: form.hasBody ? bodyText : undefined,
            },
        });
        try {
            await navigator.clipboard.writeText(url);
            showToast('Link request tersalin');
        } catch {
            showToast('Gagal menyalin link');
        }
    };

    const handleReplay = (snapshot: {
        pathParams?: Record<string, string>;
        queryParams?: Record<string, string>;
        body?: string;
    }) => {
        if (snapshot.pathParams) setPathValues(snapshot.pathParams);
        if (snapshot.queryParams) {
            setQueryValues(snapshot.queryParams);
            // Auto-include any keys from the snapshot
            setQueryIncluded((prev) => {
                const next = { ...prev };
                for (const k of Object.keys(snapshot.queryParams || {})) next[k] = true;
                return next;
            });
        }
        if (typeof snapshot.body === 'string') setBodyText(snapshot.body);
        showToast('Request dimuat dari riwayat');
    };

    return (
        <div className="space-y-5" ref={formRef}>
            {bannerVisible && (
                <SharedRequestBanner onDismiss={handleDismissBanner} />
            )}
            {noKey && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 px-3 py-2.5 rounded-md text-xs"
                    style={{ backgroundColor: 'var(--warn-bg)', border: '1px solid var(--warn-border)', color: 'var(--warn)' }}>
                    <span className="inline-flex items-start gap-2">
                        <KeyRound className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span className="leading-snug">Aktifkan API key untuk mengirim request asli.</span>
                    </span>
                    <a
                        href="https://satutim.id/settings/api"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 underline hover:no-underline shrink-0 self-start sm:self-auto"
                    >
                        Pengaturan API <ExternalLink className="h-3 w-3" />
                    </a>
                </div>
            )}

            {/* Path params */}
            {form.pathParams.length > 0 && (
                <FieldSection title="Parameter Path">
                    {form.pathParams.map((p) => (
                        <ParamRow
                            key={p.name}
                            field={p}
                            value={pathValues[p.name] || ''}
                            onChange={(v) => setPathValues((prev) => ({ ...prev, [p.name]: v }))}
                            invalid={p.required && !pathValues[p.name]}
                        />
                    ))}
                </FieldSection>
            )}

            {/* Query params */}
            {form.queryParams.length > 0 && (
                <FieldSection title="Parameter Query">
                    {form.queryParams.map((p) => (
                        <div key={p.name} className="flex items-start gap-3">
                            {!p.required && (
                                <label className="mt-2 inline-flex items-center cursor-pointer" title="Sertakan dalam request">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(queryIncluded[p.name])}
                                        onChange={(e) => setQueryIncluded((prev) => ({ ...prev, [p.name]: e.target.checked }))}
                                        aria-label={`Sertakan ${p.name}`}
                                        className="accent-emerald-500"
                                    />
                                </label>
                            )}
                            <div className="flex-1">
                                <ParamRow
                                    field={p}
                                    value={queryValues[p.name] || ''}
                                    onChange={(v) => setQueryValues((prev) => ({ ...prev, [p.name]: v }))}
                                    disabled={!p.required && !queryIncluded[p.name]}
                                />
                            </div>
                        </div>
                    ))}
                </FieldSection>
            )}

            {/* Header params (Authorization auto) */}
            <FieldSection title="Header">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs">
                    <span className="font-mono text-muted sm:w-32 sm:shrink-0">Authorization</span>
                    <input
                        type="text"
                        readOnly
                        value={key ? `Bearer ${maskKey(key)}` : ''}
                        placeholder="Bearer <YOUR_API_KEY>"
                        className="flex-1 w-full font-mono px-2.5 py-1.5 rounded text-xxs placeholder:text-ghost"
                        style={{
                            backgroundColor: 'var(--bg-input)',
                            border: '1px solid var(--border)',
                            color: key ? 'var(--ink)' : 'var(--muted)',
                        }}
                    />
                </div>
                {form.headerParams.map((p) => (
                    <ParamRow
                        key={p.name}
                        field={p}
                        value={headerValues[p.name] || ''}
                        onChange={(v) => setHeaderValues((prev) => ({ ...prev, [p.name]: v }))}
                        invalid={p.required && !headerValues[p.name]}
                    />
                ))}
            </FieldSection>

            {/* Body */}
            {form.hasBody && (
                <FieldSection
                    title="Body"
                    action={
                        <button
                            onClick={handleFormatJson}
                            className="inline-flex items-center gap-1 text-xxs text-muted hover:text-ink transition-colors px-2 py-1 rounded"
                            style={{ backgroundColor: 'var(--surface-hover)' }}
                            aria-label="Format JSON"
                        >
                            <Wand2 className="h-3 w-3" /> Format JSON
                        </button>
                    }
                >
                    <textarea
                        value={bodyText}
                        onChange={(e) => { setBodyText(e.target.value); if (bodyError) setBodyError(null); }}
                        rows={Math.min(16, Math.max(6, bodyText.split('\n').length + 1))}
                        spellCheck={false}
                        className="w-full font-mono text-xs leading-relaxed p-3 rounded resize-y"
                        style={{
                            backgroundColor: 'var(--bg-input)',
                            border: `1px solid ${bodyError ? 'var(--err)' : 'var(--border)'}`,
                            color: 'var(--ink)',
                            outline: 'none',
                        }}
                        aria-label="Request body JSON"
                        aria-invalid={Boolean(bodyError)}
                    />
                    {bodyError && (
                        <p className="text-xxs mt-1" style={{ color: 'var(--err)' }}>JSON tidak valid: {bodyError}</p>
                    )}
                </FieldSection>
            )}

            {/* Actions */}
            <div className="flex items-center flex-wrap gap-2 pt-1">
                <button
                    onClick={handleSend}
                    disabled={sendDisabled}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'var(--accent)', color: 'var(--on-accent)' }}
                    aria-label="Kirim request"
                >
                    {tryReq.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Kirim
                </button>
                <ActionMenu
                    label="Aksi"
                    icon={MoreHorizontal}
                    items={[
                        { label: 'Salin sebagai cURL', icon: Copy, onSelect: handleCopyCurl },
                        { label: 'Bagikan request (URL)', icon: Share2, onSelect: handleShare },
                    ]}
                />
                {missingRequired && (
                    <span className="text-xxs text-muted ml-1">Lengkapi parameter wajib dulu.</span>
                )}
            </div>

            {/* Response */}
            {(tryReq.busy || tryReq.response || tryReq.error) && (
                <div className="mt-6">
                    <ResponseViewer
                        busy={tryReq.busy}
                        response={tryReq.response}
                        error={tryReq.error}
                        onReset={tryReq.reset}
                    />
                </div>
            )}

            {/* History */}
            <RequestHistory operationId={operationId} onReplay={handleReplay} />
        </div>
    );
}

function FieldSection({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-ink uppercase tracking-wider">{title}</h3>
                {action}
            </div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function ParamRow({
    field,
    value,
    onChange,
    invalid = false,
    disabled = false,
}: {
    field: FormField;
    value: string;
    onChange: (v: string) => void;
    invalid?: boolean;
    disabled?: boolean;
}) {
    const placeholder = field.example !== undefined
        ? String(field.example)
        : field.default !== undefined
            ? String(field.default)
            : field.format === 'date-time'
                ? '2026-05-19T10:00:00Z'
                : field.type === 'number' ? '0'
                    : field.type === 'boolean' ? 'true/false'
                        : `<${field.name}>`;

    return (
        <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2 text-xs">
            <div className="sm:w-32 sm:shrink-0 sm:pt-1.5 flex items-baseline gap-2 sm:block">
                <label className="font-mono text-muted block">{field.name}</label>
                {field.required && <span className="text-xxs" style={{ color: 'var(--err)' }}>wajib</span>}
            </div>
            <div className="flex-1 w-full min-w-0">
                {field.type === 'select' && field.enum ? (
                    <Select
                        value={value}
                        onValueChange={onChange}
                        disabled={disabled}
                        invalid={invalid}
                        ariaLabel={field.name}
                        options={field.enum.map((opt) => ({ value: String(opt), label: String(opt) }))}
                    />
                ) : (
                    <input
                        type={field.type === 'number' ? 'number' : 'text'}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        disabled={disabled}
                        aria-label={field.name}
                        aria-required={field.required}
                        aria-invalid={invalid}
                        className="w-full font-mono px-2.5 py-1.5 rounded text-xxs"
                        style={{
                            backgroundColor: 'var(--bg-input)',
                            border: `1px solid ${invalid ? 'var(--err)' : 'var(--border)'}`,
                            color: 'var(--ink)',
                            opacity: disabled ? 0.5 : 1,
                            outline: 'none',
                        }}
                    />
                )}
                {field.description && (
                    <p className="text-xxs text-ghost mt-1">{field.description}</p>
                )}
            </div>
        </div>
    );
}

function maskKey(k: string): string {
    if (k.length <= 12) return k;
    return `${k.slice(0, 8)}…${k.slice(-4)}`;
}
